import { useEffect, useMemo, useState } from "react";
import clsx from "clsx";
import type { SubmissionStatus } from "@edge/shared";
import { ApiError, patchSubmission } from "../../../api";
import {
  ALL_STATUSES,
  STATUS_LABELS,
  STATUS_PILL_LABELS,
  VALID_TRANSITIONS,
  requiresReason,
} from "../../../lib/submission-status";

interface ChangeStatusDialogProps {
  submissionId: string;
  lenderName: string;
  currentStatus: SubmissionStatus;
  /** Throws on failure. */
  onUpdated: (newStatus: SubmissionStatus) => Promise<void> | void;
  onCancel: () => void;
}

/**
 * Status-change dialog. Picker groups suggested transitions at the top,
 * remaining statuses below — but anything can be picked (state-machine
 * is loose, not enforced). When target is `declined` or `backup`, a
 * required reason textarea appears. Optional note added to chronology
 * atomically via the worker's PATCH+touch endpoint.
 */
export function ChangeStatusDialog({
  submissionId,
  lenderName,
  currentStatus,
  onUpdated,
  onCancel,
}: ChangeStatusDialogProps) {
  const suggested = useMemo(
    () => VALID_TRANSITIONS[currentStatus],
    [currentStatus],
  );
  const other = useMemo(
    () =>
      ALL_STATUSES.filter(
        (s) => s !== currentStatus && !suggested.includes(s),
      ),
    [currentStatus, suggested],
  );

  // Default the picker to the first suggested transition if any, else blank.
  const [target, setTarget] = useState<SubmissionStatus | "">(
    suggested[0] ?? "",
  );
  const [reason, setReason] = useState("");
  const [note, setNote] = useState("");
  const [running, setRunning] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      if (e.key === "Escape" && !running) onCancel();
    };
    window.addEventListener("keydown", handler);
    return () => window.removeEventListener("keydown", handler);
  }, [onCancel, running]);

  const reasonKind = target ? requiresReason(target) : null;
  const reasonRequired = reasonKind !== null;
  const canSave =
    target !== "" &&
    target !== currentStatus &&
    (!reasonRequired || reason.trim().length > 0) &&
    !running;

  const handleSave = async () => {
    if (target === "") return;
    setError(null);
    setRunning(true);
    try {
      const trimmedReason = reason.trim();
      const trimmedNote = note.trim();
      await patchSubmission(submissionId, {
        status: target,
        decline_reason: reasonKind === "decline" ? trimmedReason : undefined,
        backup_reason: reasonKind === "backup" ? trimmedReason : undefined,
        touch: trimmedNote
          ? { body: trimmedNote, direction: "edge" }
          : undefined,
      });
      await onUpdated(target);
    } catch (err) {
      setError(
        err instanceof ApiError
          ? `${err.code} (HTTP ${err.status})`
          : err instanceof Error
            ? err.message
            : "update_failed",
      );
      setRunning(false);
    }
  };

  return (
    <div className="write-confirm-backdrop" role="dialog" aria-modal="true">
      <div className="write-confirm-card submission-status-card">
        <div className="write-confirm-title">Change status — {lenderName}</div>

        <div className="submission-status-flow">
          <span
            className={clsx(
              "status-pill",
              `status-${currentStatus.replace("_", "-")}`,
            )}
          >
            {STATUS_PILL_LABELS[currentStatus]}
          </span>
          <span className="submission-status-arrow">→</span>
          <select
            className="submission-form-input submission-status-select"
            value={target}
            onChange={(e) => setTarget(e.target.value as SubmissionStatus)}
            autoFocus
          >
            <option value="">Pick new status…</option>
            {suggested.length > 0 && (
              <optgroup label="── SUGGESTED ──">
                {suggested.map((s) => (
                  <option key={s} value={s}>
                    {STATUS_LABELS[s]}
                  </option>
                ))}
              </optgroup>
            )}
            {other.length > 0 && (
              <optgroup label="── OTHER ──">
                {other.map((s) => (
                  <option key={s} value={s}>
                    {STATUS_LABELS[s]}
                  </option>
                ))}
              </optgroup>
            )}
          </select>
        </div>

        {reasonKind && (
          <label className="submission-form-field">
            <span className="submission-form-label">
              {reasonKind === "decline" ? "Decline reason" : "Backup reason"}
              <span className="submission-form-required">·required</span>
            </span>
            <textarea
              className="submission-form-textarea"
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              rows={3}
              placeholder={
                reasonKind === "decline"
                  ? "e.g. below LTV requirement, max 60 LTV on this asset class"
                  : "e.g. Plan B if Lender A blows up — terms were 25bps wider"
              }
              autoFocus
            />
          </label>
        )}

        <label className="submission-form-field">
          <span className="submission-form-label">
            Note added to chronology
            <span className="submission-form-hint">·optional</span>
          </span>
          <textarea
            className="submission-form-textarea"
            value={note}
            onChange={(e) => setNote(e.target.value)}
            rows={2}
            placeholder="Captured as an 'edge' touch alongside the status change."
          />
        </label>

        {error && <div className="write-confirm-error">⚠ {error}</div>}

        <div className="write-confirm-actions">
          <button
            className="write-confirm-cancel"
            onClick={onCancel}
            disabled={running}
          >
            CANCEL
          </button>
          <button
            className="write-confirm-confirm"
            onClick={handleSave}
            disabled={!canSave}
          >
            {running ? "WRITING…" : "UPDATE STATUS"}
          </button>
        </div>
      </div>
    </div>
  );
}
